import { rosterShift } from '../data/roster-shifts'
import { formatShortDate, formatTimeRange } from '../lib/format-date'
import type { FeedEntry } from '../lib/feed'
import { FitBadge } from './FitBadge'
import { ShiftCodeChip } from './ShiftCodeChip'

export interface FeedCardProps {
  entry: FeedEntry
}

/** D2's honest labels — display only; NEED_OFF still resolves as a 1:1 swap in Phase 5. */
const INTENT_LABEL = {
  OPEN_TO_SWAP: 'Open to swap',
  NEED_OFF: 'Needs the day off',
} as const

/**
 * One advertised shift as a card, in Station Ledger's idiom and migratable
 * back into it (the plan's vendor rule): date, shift-code chip, times, who is
 * advertising it and from which block, the flags, the note, and — the point
 * of the card — the `FitBadge` with its reason.
 *
 * One action exists in Phase 4: the Messenger deep link, rendered only when
 * the advertiser recorded a username (C4). Offering a shift in return is
 * Phase 5's; the link is how the pair talks meanwhile.
 *
 * The advertiser's block is shown because §3.3's arithmetic lives in blocks:
 * a 2B paramedic reading a 4A card can do the two-clean-partners maths
 * themselves, and the badge underneath already did it for them.
 */
export function FeedCard({ entry }: FeedCardProps) {
  const { item, verdict } = entry
  const times = rosterShift(item.shiftCode)

  return (
    <article className="ss-feed-card">
      <div className="ss-feed-card__when">
        <ShiftCodeChip code={item.shiftCode} />
        <span className="ss-feed-card__date">{formatShortDate(item.workDate)}</span>
        <span className="ss-feed-card__time">
          {formatTimeRange(times.startMin, times.endMin)}
        </span>
      </div>

      <div className="ss-feed-card__who">
        <span className="ss-feed-card__name">
          {item.displayName ?? 'A colleague'}
          {item.blockCode !== null ? <span className="ss-feed-card__block"> · {item.blockCode}</span> : null}
        </span>
        {item.classification !== null ? (
          <span className="ss-feed-card__classification">{item.classification}</span>
        ) : null}
      </div>

      <div className="ss-feed-card__flags">
        <span className="ss-feed-card__flag">{INTENT_LABEL[item.intent]}</span>
        {item.noNightSwap ? (
          <span className="ss-feed-card__flag" title="This advertiser will not swap for a night shift">
            No nights in return
          </span>
        ) : null}
      </div>

      {item.note !== null && item.note !== '' ? (
        <p className="ss-feed-card__note">{item.note}</p>
      ) : null}

      <div className="ss-feed-card__foot">
        <FitBadge verdict={verdict} />
        {item.facebookUsername !== null && item.facebookUsername !== '' ? (
          <a
            className="sl-btn sl-btn--secondary"
            href={`https://m.me/${item.facebookUsername}`}
            target="_blank"
            rel="noreferrer"
          >
            Message on Messenger
          </a>
        ) : null}
      </div>
    </article>
  )
}
